/**
 * Chips e helpers visuais no padrão Tiflux (antd v5): trios tintados
 * bg + borda + texto, radius 4px, 22px de altura. Os hexes aqui são
 * intencionais — a spec fixa as cores exatas dos chips de status e não
 * há token semântico correspondente.
 */
import type { ReactNode } from 'react';
import { InboxIcon } from 'lucide-react';
import type { StatusKind } from '@erp/shared';
import { cn } from '@/lib/utils';
import { SLA_STATE_LABELS, type SlaState } from './ticket-sla';
import { STATUS_KIND_CLASSES } from './detail-status-chip';

const CHIP_BASE =
  'inline-flex h-[22px] w-fit shrink-0 items-center rounded-[4px] border px-2 text-xs font-medium whitespace-nowrap';

// ── Status: as MESMAS cores do detalhe (Aberto azul / Fechado verde /
// Cancelado vermelho, DESIGN-V2) — uma fonte só, em detail-status-chip ─────
export function StatusChip({
  kind,
  name,
  className,
}: {
  kind: StatusKind;
  name: string;
  className?: string;
}) {
  return <span className={cn(CHIP_BASE, STATUS_KIND_CLASSES[kind], className)}>{name}</span>;
}

// ── Prioridade (heurística por nome, cores de warning/erro antd) ────────────
type PriorityTone = 'high' | 'medium' | 'low';

function priorityTone(name: string): PriorityTone {
  const n = name.toLowerCase();
  if (n.includes('urg') || n.includes('alta') || n.includes('crít') || n.includes('crit')) {
    return 'high';
  }
  if (n.includes('méd') || n.includes('med') || n.includes('normal')) return 'medium';
  return 'low';
}

const PRIORITY_CHIP: Record<PriorityTone, string> = {
  high: 'border-[#FFA39E] bg-[#FFF1F0] text-[#FF4D4F]',
  medium: 'border-[#FFE58F] bg-[#FFFBE6] text-[#FAAD14]',
  low: 'border-border bg-muted text-muted-foreground',
};

export function PriorityChip({ name, className }: { name: string; className?: string }) {
  return <span className={cn(CHIP_BASE, PRIORITY_CHIP[priorityTone(name)], className)}>{name}</span>;
}

// ── SLA (v3) — mesmos trios tintados dos chips de status ────────────────────
const SLA_CHIP: Record<SlaState, string> = {
  paused: 'border-[#91CAFF] bg-[#E6F4FF] text-[#1677FF]',
  met: 'border-[#B7EB8F] bg-[#F6FFED] text-[#52C41A]',
  overdue: 'border-[#FFA39E] bg-[#FFF1F0] text-[#FF4D4F]',
  due_soon: 'border-[#FFE58F] bg-[#FFFBE6] text-[#FAAD14]',
  on_track: 'border-border bg-muted text-muted-foreground',
};

export function SlaChip({ state, className }: { state: SlaState; className?: string }) {
  return (
    <span className={cn(CHIP_BASE, SLA_CHIP[state], className)}>{SLA_STATE_LABELS[state]}</span>
  );
}

// ── Pill de contagem (colunas do kanban, contadores) ────────────────────────
export function CountPill({ count, className }: { count: number; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-muted-foreground/15 px-1.5 text-xs font-medium tabular-nums text-muted-foreground',
        className
      )}
    >
      {count}
    </span>
  );
}

// ── Empty state Tiflux ("Não há dados") ─────────────────────────────────────
export function EmptyData({
  message = 'Não há dados',
  children,
  className,
}: {
  message?: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground',
        className
      )}
    >
      <InboxIcon className="size-8 opacity-40" strokeWidth={1.5} />
      <span className="text-sm">{message}</span>
      {children}
    </div>
  );
}
